import { action, computed, makeObservable, observable } from "mobx";
import localStorage from "./localStorage.js";
import { isChunkParsed, walkModules } from "./utils.js";

export class Store {
  cid = 0;

  sizes = new Set([
    "statSize",
    "parsedSize",
    "gzipSize",
    "brotliSize",
    "zstdSize",
  ]);

  allChunks;

  selectedChunks;

  searchQuery = "";

  defaultSize;

  selectedSize;

  showConcatenatedModulesContent =
    localStorage.getItem("showConcatenatedModulesContent") === true;

  darkMode = (() => {
    const systemPrefersDark = globalThis.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;

    try {
      const saved = localStorage.getItem("darkMode");
      if (saved !== null) return saved === "true";
    } catch {
      // Some browsers might not have localStorage available and we can fail silently
    }

    return systemPrefersDark;
  })();

  constructor() {
    makeObservable(this, {
      allChunks: observable.ref,
      selectedChunks: observable.shallow,
      searchQuery: observable,
      defaultSize: observable,
      selectedSize: observable,
      showConcatenatedModulesContent: observable,
      darkMode: observable,

      toggleDarkMode: action,
      setModules: action,
      setSelectedChunks: action,
      setSelectedSize: action,
      setSearchQuery: action,

      hasParsedSizes: computed,
      activeSize: computed,
      visibleChunks: computed,
      allChunksSelected: computed,
      totalChunksSize: computed,
      searchQueryRegexp: computed,
      isSearching: computed,
      foundModulesByChunk: computed,
      foundModules: computed,
      hasFoundModules: computed,
      hasConcatenatedModules: computed,
      foundModulesSize: computed,
    });
  }

  setModules(modules) {
    walkModules(modules, (module) => {
      module.cid = this.cid++;
    });

    this.allChunks = modules;
    this.selectedChunks = this.allChunks;
  }

  setEntrypoints(entrypoints) {
    this.entrypoints = entrypoints;
  }

  get hasParsedSizes() {
    return this.allChunks.some(isChunkParsed);
  }

  setSelectedSize(selectedSize) {
    this.selectedSize = selectedSize;
  }

  get activeSize() {
    const activeSize = this.selectedSize || this.defaultSize;

    if (!this.hasParsedSizes || !this.sizes.has(activeSize)) {
      return "statSize";
    }

    return activeSize;
  }

  setSelectedChunks(chunks) {
    this.selectedChunks = chunks;
  }

  get visibleChunks() {
    const visibleChunks = this.allChunks.filter((chunk) =>
      this.selectedChunks.includes(chunk),
    );

    return this.filterModulesForSize(visibleChunks, this.activeSize);
  }

  get allChunksSelected() {
    return this.visibleChunks.length === this.allChunks.length;
  }

  get totalChunksSize() {
    return this.allChunks.reduce(
      (totalSize, chunk) => totalSize + (chunk[this.activeSize] || 0),
      0,
    );
  }

  get searchQueryRegexp() {
    const query = this.searchQuery.trim();

    if (!query) {
      return null;
    }

    try {
      return new RegExp(query, "iu");
    } catch {
      return null;
    }
  }

  get isSearching() {
    return Boolean(this.searchQueryRegexp);
  }

  get foundModulesByChunk() {
    if (!this.isSearching) {
      return [];
    }

    const query = this.searchQueryRegexp;

    return this.visibleChunks
      .map((chunk) => {
        let foundGroups = [];

        walkModules(chunk.groups, (module) => {
          let weight = 0;

          /**
           * Splitting found modules/directories into groups:
           *
           * 1) Module with matched label (weight = 4)
           * 2) Directory with matched label (weight = 3)
           * 3) Module with matched path (weight = 2)
           * 4) Directory with matched path (weight = 1)
           */
          if (query.test(module.label)) {
            weight += 3;
          } else if (module.path && query.test(module.path)) {
            weight++;
          }

          if (!weight) return;

          if (!module.groups) {
            weight += 1;
          }

          const foundModules = (foundGroups[weight - 1] =
            foundGroups[weight - 1] || []);
          foundModules.push(module);
        });

        const { activeSize } = this;

        // Filtering out missing groups
        foundGroups = foundGroups.filter(Boolean).reverse();
        // Sorting each group by active size
        for (const modules of foundGroups) {
          modules.sort((m1, m2) => m2[activeSize] - m1[activeSize]);
        }

        return {
          chunk,
          modules: foundGroups.flat(),
        };
      })
      .filter((result) => result.modules.length > 0)
      .toSorted((c1, c2) => c1.modules.length - c2.modules.length);
  }

  setSearchQuery(query) {
    this.searchQuery = query;
  }

  get foundModules() {
    return this.foundModulesByChunk.reduce(
      (arr, chunk) => [...arr, ...chunk.modules],
      [],
    );
  }

  get hasFoundModules() {
    return this.foundModules.length > 0;
  }

  get hasConcatenatedModules() {
    let result = false;

    walkModules(this.visibleChunks, (module) => {
      if (module.concatenated) {
        result = true;
        return false;
      }
    });

    return result;
  }

  get foundModulesSize() {
    return this.foundModules.reduce(
      (summ, module) => summ + module[this.activeSize],
      0,
    );
  }

  filterModulesForSize(modules, sizeProp) {
    return modules.reduce((filteredModules, module) => {
      if (module[sizeProp]) {
        if (module.groups) {
          const showContent =
            !module.concatenated || this.showConcatenatedModulesContent;

          module = {
            ...module,
            groups: showContent
              ? this.filterModulesForSize(module.groups, sizeProp)
              : null,
          };
        }

        module.weight = module[sizeProp];
        filteredModules.push(module);
      }

      return filteredModules;
    }, []);
  }

  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    try {
      localStorage.setItem("darkMode", this.darkMode);
    } catch {
      // Some browsers might not have localStorage available and we can fail silently
    }
    this.updateTheme();
  }

  updateTheme() {
    if (this.darkMode) {
      document.documentElement.dataset.theme = "dark";
    } else {
      delete document.documentElement.dataset.theme;
    }
  }
}

export const store = new Store();
